import { fetchWithAuthRelogin } from '../utils/sessionRelogin';
import { getMiniappAccessToken } from '../utils/miniappAccessToken';

/**
 * AI-примерка: POST /api/tryon/generate (multipart).
 */

function normalizeTryOnBase(raw) {
  if (!raw || typeof raw !== 'string') return '';
  return raw.replace(/\/+$/, '');
}

/**
 * База API примерки (без хвостового /).
 * Можно задать REACT_APP_TRYON_API_BASE (например https://miniapp.example.com/api/tryon), иначе origin + /api/tryon.
 */
export function getTryOnApiBase() {
  if (typeof window === 'undefined') return '';
  const fromEnv = process.env.REACT_APP_TRYON_API_BASE;
  if (fromEnv && String(fromEnv).trim()) {
    return normalizeTryOnBase(String(fromEnv).trim());
  }
  return normalizeTryOnBase(`${window.location.origin}/api/tryon`);
}

function getTryonAuthHeaders() {
  const token = getMiniappAccessToken();
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

function formatTryOnErrorDetail(data, status) {
  if (typeof data.detail === 'string') return data.detail;
  if (Array.isArray(data.detail)) {
    return data.detail
      .map((d) => (typeof d === 'object' && d.msg ? d.msg : String(d)))
      .join(', ');
  }
  if (data.detail != null && typeof data.detail === 'object') {
    return JSON.stringify(data.detail);
  }
  return `Ошибка ${status}`;
}

/**
 * @param {File} userPhoto
 * @param {Array<{ image_url: string, type: string, description?: string }>} items
 * @param {{ aspect_ratio?: string, debug?: boolean }} [opts]
 */
export async function postTryOnGenerate(userPhoto, items, opts = {}) {
  const fd = new FormData();
  fd.append('user_photo', userPhoto);
  fd.append('items_json', JSON.stringify(items));
  if (opts.aspect_ratio) fd.append('aspect_ratio', opts.aspect_ratio);
  if (opts.debug) fd.append('debug', 'true');

  const res = await fetchWithAuthRelogin(`${getTryOnApiBase()}/generate`, {
    method: 'POST',
    body: fd,
    headers: getTryonAuthHeaders(),
  });
  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    data = { detail: text || 'Ошибка сервера' };
  }
  if (!res.ok) {
    const msg =
      typeof data.detail === 'string'
        ? data.detail
        : Array.isArray(data.detail)
          ? data.detail.map((d) => d.msg || d).join(', ')
          : `Ошибка ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data;
}

/**
 * Список сохранённых примерок на сервере.
 * @param {number} [limit]
 * @returns {Promise<{ items: Array<{ id: string, result_url: string, created_at?: string, size_bytes?: number }> }>}
 */
export async function fetchTryOnResults(limit = 50) {
  const base = getTryOnApiBase();
  const q = `?limit=${encodeURIComponent(limit)}`;

  async function tryOne(pathSuffix, headers = {}) {
    const res = await fetchWithAuthRelogin(`${base}${pathSuffix}${q}`, {
      headers: { ...headers },
    });
    const text = await res.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch {
      data = { detail: text || 'Ошибка' };
    }
    return { res, data };
  }

  const authHeaders = getTryonAuthHeaders();
  let { res, data } = await tryOne('/results', authHeaders);
  if (res.status === 404) {
    ({ res, data } = await tryOne('/list-results', authHeaders));
  }
  if (!res.ok) {
    const msg = formatTryOnErrorDetail(data, res.status);
    const err = new Error(
      res.status === 404
        ? `${msg} — обновите tryon-service на сервере (нужны GET /results или /list-results).`
        : msg,
    );
    err.status = res.status;
    throw err;
  }
  return data;
}

/** Добавляет access_token для GET без заголовка Authorization (Telegram WebView). */
function appendTryonAccessToken(url) {
  const token = getMiniappAccessToken();
  if (token) {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}access_token=${encodeURIComponent(token)}`;
  }
  return url;
}

/**
 * Превью оригинала примерки (только с токеном в query).
 * @param {string} resultId
 * @param {string|null|undefined} [accessToken] — если не передан, берётся из getMiniappAccessToken() (для галереи лучше передать token из useAuth).
 */
export function getTryOnImageUrl(resultId, accessToken) {
  const base = `${getTryOnApiBase()}/result/${encodeURIComponent(resultId)}/file`;
  const token = accessToken !== undefined ? accessToken : getMiniappAccessToken();
  if (token) {
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}access_token=${encodeURIComponent(token)}`;
  }
  return base;
}

/** URL скачивания с водяным знаком. */
export function getTryOnDownloadUrl(resultId) {
  const base = `${getTryOnApiBase()}/result/${encodeURIComponent(resultId)}/download`;
  return appendTryonAccessToken(base);
}
